import { FeatureStatus, PES_FLAGS } from '../constants';
import { CustomWindow, FeatureAttributes, PesFeatureFlags } from '../types/common';
import { PBSConfigs } from '../types/services/globalConfigs';

export class ConfiguratorHandler {
  private static id = 'pesConfigs';

  static isFeatureFlagEnabled(flag: FeatureAttributes): boolean {
    return flag.status === FeatureStatus.ENABLED;
  }

  static getSingleConfig<
    T extends keyof CustomWindow['pesConfigs'],
    K extends CustomWindow['pesConfigs'][T],
  >(configName: T): K {
    return window[ConfiguratorHandler.id]?.[configName] ?? null;
  }

  static setSingleConfig<
    T extends keyof CustomWindow['pesConfigs'],
    K extends CustomWindow['pesConfigs'][T],
  >(configName: T, configValue: K): void {
    if (typeof window[ConfiguratorHandler.id] !== 'object' || window[ConfiguratorHandler.id] === null) {
      console.warn(`[PES]: window object did not have ${ConfiguratorHandler.id} initialized.`);
      window[ConfiguratorHandler.id] = {};
    }
    window[ConfiguratorHandler.id][configName] = configValue;
  }

  static getLocalizedEnabledFeatures(
    features: PBSConfigs['LocalizedFeatures'],
    locale: string,
  ): PesFeatureFlags {
    const result: PesFeatureFlags = {
      [PES_FLAGS.Substitutions]: { status: FeatureStatus.DISABLED },
      [PES_FLAGS.UnifiedCheckout]: { status: FeatureStatus.DISABLED },
    };

    // eslint-disable-next-line no-restricted-syntax
    for (const [ key, attributes ] of Object.entries(features)) {
      if (attributes.locales !== '*' && !attributes.locales.includes(locale)) {
        result[PES_FLAGS[key]] = { status: FeatureStatus.DISABLED };
        continue;
      }

      result[PES_FLAGS[key]] = {
        ...attributes,
        status: FeatureStatus.ENABLED,
      };

      delete result[PES_FLAGS[key]].locales;
    }

    return result;
  }

  static setupGlobalConfigs(configs: PBSConfigs, country: string, language: string): void {
    if (!country || !language) {
      console.warn('[PES]: country or language was not provided.');
      return;
    }

    const currentLocale = `${country}_${language}`.toLowerCase();
    const Widgets = {};
    const { Widgets: widgetConf } = configs;

    if (widgetConf) {
      Object.entries(widgetConf).forEach(([ widgetType, widgetConfigs ]) => {
        const currentLocalWidget = (widgetConfigs)?.find(
          (config) => config?.locales === '*' || (Array.isArray(config?.locales) && config?.locales.includes(currentLocale)),
        );
        if (!currentLocalWidget?.attributes) return;
        Widgets[widgetType] = currentLocalWidget?.attributes;
      });
    }

    const pesConfigs: CustomWindow['pesConfigs'] = {};
    if (configs.LocalizedFeatures) {
      pesConfigs.FeatureFlags = ConfiguratorHandler.getLocalizedEnabledFeatures(
        configs.LocalizedFeatures,
        currentLocale,
      );
    }

    if (Object.keys(Widgets).length) { pesConfigs.Widgets = Widgets; }
    if (configs.api) { pesConfigs.API = configs.api; }
    window[ConfiguratorHandler.id] = pesConfigs;
  }
}
